import React, { Component } from 'react'

const CANVAS_MARGIN = 16
const CANVAS_SHADOW = 16
const OFFSET_Y_MIN_RATE = 0.1
const OFFSET_ANIMATION_DURATION_PER_RATE = 250

class ClozeImage extends Component {
  constructor(props) {
    super(props)

    this.offsetAnimation = null
    this.moving = null
    this.canvasRef = React.createRef()
  }

  componentDidMount() {
    window.requestAnimationFrame(() => this.drawDisplay())
  }

  componentDidUpdate(prevProps) {
    if (this.props.imageId !== prevProps.imageId) {
      this.offsetAnimation = null
    }
    window.requestAnimationFrame(() => this.drawDisplay())
  }

  imageClicked(event) {
    if (!this.props.onImageClick) {
      return
    }
    this.props.onImageClick()
  }

  getFocusedBottomOffset(canvas, image) {
    if (this.props.imageFocused) {
      return 0
    }
    const bounds = this.props.pages[this.props.currentPage].bounds.filter(
      (bound) =>
        bound.rowIndex === this.props.selectedRow &&
        bound.beginIndex === this.props.selectedBegin
    )
    const ys = bounds
      .map((bound) => {
        return {
          upper: Math.min(...bound.area.map((area) => area[1] + area[3])),
          lower: Math.max(...bound.area.map((area) => area[1] + area[3]))
        }
      })
      .map((heights) => (heights.lower - heights.upper) / 2.0 + heights.upper)
      .reduce((a, b) => a + b, 0)
    const offsetY = Math.ceil(image.height / 2.0 - ys)
    const preferY = Math.min(offsetY, image.height * OFFSET_Y_MIN_RATE)
    if (!this.moving) {
      return preferY
    }
    return preferY + (this.moving.endClientY - this.moving.startClientY)
  }

  hasNextAnimation() {
    if (!this.offsetAnimation) {
      return false
    }
    if (this.offsetAnimation.y !== this.offsetAnimation.toY) {
      return true
    }
    return false
  }

  isAlreadyAnswered(rowIndex, beginIndex) {
    const { onAnswersCheck } = this.props
    if (!onAnswersCheck) {
      return false
    }
    return onAnswersCheck(rowIndex, beginIndex)
  }

  imageTouchStart(event) {
    if (this.moving) {
      return
    }
    if (event.touches.length !== 1) {
      return
    }
    const touch = event.touches[0]
    this.moving = {
      startClientY: touch.clientY,
      endClientY: touch.clientY
    }
    window.requestAnimationFrame(() => this.drawDisplay())
  }

  imageTouchFinish(event) {
    if (!this.moving) {
      return
    }
    this.moving = null
    window.requestAnimationFrame(() => this.drawDisplay())
  }

  imageTouchMove(event) {
    if (!this.moving) {
      return
    }
    if (event.touches.length !== 1) {
      return
    }
    const touch = event.touches[0]
    this.moving.endClientY = touch.clientY
    window.requestAnimationFrame(() => this.drawDisplay())
  }

  drawDisplay() {
    const canvas = this.canvasRef.current
    const image = this.props.imageRef.current
    if (!canvas || !image) {
      return
    }
    canvas.width = image.width
    canvas.height = image.height

    const ctx = canvas.getContext('2d')
    const toOffsetY = this.getFocusedBottomOffset(canvas, image)
    if ((this.offsetAnimation || { toY: 0 }).toY !== toOffsetY) {
      const start = Date.now()
      const fromY = (this.offsetAnimation || { y: 0 }).y
      const moveRate = Math.abs(toOffsetY - fromY) / image.height
      this.offsetAnimation = {
        y: fromY,
        fromY,
        toY: toOffsetY,
        start,
        duration: Math.ceil(OFFSET_ANIMATION_DURATION_PER_RATE * moveRate)
      }
    } else if (this.offsetAnimation !== null) {
      const elapsed = Date.now() - this.offsetAnimation.start
      if (elapsed > this.offsetAnimation.duration) {
        this.offsetAnimation.y = this.offsetAnimation.toY
      } else {
        const rate = elapsed / this.offsetAnimation.duration
        this.offsetAnimation.y =
          (this.offsetAnimation.toY - this.offsetAnimation.fromY) * rate +
          this.offsetAnimation.fromY
      }
    }
    const offsetY = (this.offsetAnimation || { y: 0 }).y
    if (offsetY > 0) {
      ctx.fillStyle = '#ccc'
      ctx.fillRect(0, 0, canvas.width, offsetY - CANVAS_MARGIN)
      const gradient = ctx.createLinearGradient(
        0,
        offsetY - CANVAS_MARGIN - CANVAS_SHADOW,
        0,
        offsetY - CANVAS_MARGIN
      )
      gradient.addColorStop(0, '#ccc')
      gradient.addColorStop(1, '#888')
      ctx.fillStyle = gradient
      ctx.fillRect(
        0,
        offsetY - CANVAS_MARGIN - CANVAS_SHADOW,
        canvas.width,
        CANVAS_SHADOW
      )
    } else if (offsetY < 0) {
      ctx.fillStyle = '#ccc'
      ctx.fillRect(
        0,
        canvas.height + offsetY + CANVAS_MARGIN,
        canvas.width,
        -offsetY - CANVAS_MARGIN
      )
      const gradient = ctx.createLinearGradient(
        0,
        canvas.height + offsetY + CANVAS_MARGIN + CANVAS_SHADOW,
        0,
        canvas.height + offsetY + CANVAS_MARGIN
      )
      gradient.addColorStop(0, '#ccc')
      gradient.addColorStop(1, '#888')
      ctx.fillStyle = gradient
      ctx.fillRect(
        0,
        canvas.height + offsetY + CANVAS_MARGIN,
        canvas.width,
        CANVAS_SHADOW
      )
    }
    ctx.drawImage(image, 0, offsetY)

    if (
      this.props.imageFocused ||
      !this.props.pages ||
      !this.props.pages[this.props.currentPage]
    ) {
      if (this.hasNextAnimation()) {
        window.requestAnimationFrame(() => this.drawDisplay())
      }
      return
    }
    this.props.pages[this.props.currentPage].bounds.forEach((bound) => {
      if (!this.isAlreadyAnswered(bound.rowIndex, bound.beginIndex)) {
        const x1 = Math.min(...bound.area.map((area) => area[0]))
        const y1 = Math.min(...bound.area.map((area) => area[1]))
        const x2 = Math.max(...bound.area.map((area) => area[0] + area[2]))
        const y2 = Math.max(...bound.area.map((area) => area[1] + area[3]))
        ctx.fillStyle = 'white'
        ctx.fillRect(x1, y1 + offsetY, x2 - x1, y2 - y1)
      }
    })
    this.props.pages[this.props.currentPage].bounds.forEach((bound) => {
      if (
        bound.rowIndex === this.props.selectedRow &&
        bound.beginIndex === this.props.selectedBegin
      ) {
        const x1 = Math.min(...bound.area.map((area) => area[0]))
        const y1 = Math.min(...bound.area.map((area) => area[1]))
        const x2 = Math.max(...bound.area.map((area) => area[0] + area[2]))
        const y2 = Math.max(...bound.area.map((area) => area[1] + area[3]))
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)'
        ctx.lineWidth = 4
        ctx.strokeRect(x1, y1 + offsetY, x2 - x1, y2 - y1)
      }
    })
    if (this.hasNextAnimation()) {
      window.requestAnimationFrame(() => this.drawDisplay())
    }
  }

  render() {
    return (
      <canvas
        className={this.props.canvasClassName}
        ref={this.canvasRef}
        onClick={(e) => this.imageClicked(e)}
        onTouchStart={(e) => this.imageTouchStart(e)}
        onTouchEnd={(e) => this.imageTouchFinish(e)}
        onTouchCancel={(e) => this.imageTouchFinish(e)}
        onTouchMove={(e) => this.imageTouchMove(e)}
      />
    )
  }
}

export default ClozeImage
